import type { ClientGame, ClientGameCtx } from "./index";
import { SNL_N, SNL_GOAL, SNL_LADDERS, SNL_SNAKES, snlCell } from "../../shared/snl";
import { sound } from "../sound";
import { C, TINT, SEAT_COLORS, bodyFont } from "../theme";
import { groundShadow, puck, roundRectPath } from "../draw";

// a bright sticker board (theme.ts palette): pale-tint checkerboard, wooden
// ladders and candy-coloured snakes with ink outlines, seat-coloured tokens
const TOK: string[] = [SEAT_COLORS[0], SEAT_COLORS[1]];
const ROW_TINTS: string[] = [TINT.mango, TINT.mint, TINT.cobalt, TINT.bubblegum, TINT.grape, TINT.coral];
const SNAKE_COLS: string[] = [C.coral, C.mint, C.grape, C.mango, C.bubblegum, C.cobalt, C.green, "#FF8A3C"];
const LADDER = "#E3A564";
/** canvas px kept around the board for its outline, and the hard shadow's drop */
const EDGE = 3;
const DROP = 4;

export class SnlClient implements ClientGame {
  private ctx!: ClientGameCtx;
  private canvas!: HTMLCanvasElement;
  private rollBtn!: HTMLButtonElement;
  private statusEl!: HTMLDivElement;
  private raf = 0;
  private last = performance.now();
  private lastRoll = 0;
  private animT = 0;
  private tumbleT = 0;

  private get st() { return this.ctx.room.state.snl; }
  private get mine() { return this.ctx.mySlot === 1 ? 1 : 0; }
  private get myTurn() { return this.st.turnSlot === this.mine && this.st.phase === "roll"; }

  mount(ctx: ClientGameCtx) {
    this.ctx = ctx;
    sound.unlock();

    const wrap = document.createElement("div");
    wrap.className = "snl-wrap";
    this.canvas = document.createElement("canvas");
    this.canvas.className = "snl-board";
    this.statusEl = document.createElement("div");
    this.statusEl.className = "snl-status";
    this.rollBtn = document.createElement("button");
    this.rollBtn.className = "snl-roll";
    this.rollBtn.textContent = "ROLL";
    this.rollBtn.onclick = () => {
      if (!this.myTurn) return;
      sound.unlock();
      this.ctx.room.send("roll");
    };
    wrap.append(this.statusEl, this.canvas, this.rollBtn);
    ctx.root.appendChild(wrap);

    ctx.room.onMessage("snl-roll", (m: any) => {
      sound.snlRoll();
      this.tumbleT = 480;
      if (m?.slot === this.mine) try { navigator.vibrate?.(15); } catch { /* */ }
    });
    ctx.room.onMessage("snl-ladder", () => sound.snlLadder());
    ctx.room.onMessage("snl-snake", (m: any) => {
      sound.snlSnake();
      if (m?.slot === this.mine) try { navigator.vibrate?.([20, 30, 40]); } catch { /* */ }
    });

    this.loop();
  }

  unmount() {
    cancelAnimationFrame(this.raf);
  }

  private loop = () => {
    const now = performance.now();
    const dt = Math.min(50, now - this.last);
    this.last = now;
    if (this.st.rollNo !== this.lastRoll) { this.lastRoll = this.st.rollNo; this.animT = 0; }
    if (this.st.phase === "moving") this.animT += dt;
    if (this.tumbleT > 0) this.tumbleT -= dt;
    try { this.draw(); } catch (e) { console.error("snl frame", e); }
    this.raf = requestAnimationFrame(this.loop);
  };

  // squares walked from -> land, including a bounce off 100 on an overshoot
  private path(): number[] {
    const st = this.st;
    const from = st.animFrom, land = st.animLand, die = st.die;
    const peak = from + die;
    const out: number[] = [];
    if (peak <= SNL_GOAL) {
      for (let s = from + 1; s <= land; s++) out.push(s);
    } else {
      for (let s = from + 1; s <= SNL_GOAL; s++) out.push(s);
      for (let s = SNL_GOAL - 1; s >= land; s--) out.push(s);
    }
    return out.length ? out : [land];
  }

  private draw() {
    const st = this.st;
    const c = this.canvas;
    const g = c.getContext("2d")!;
    const dpr = Math.min(2, devicePixelRatio || 1);
    const cw = c.clientWidth, ch = c.clientHeight;
    if (cw < 40 || ch < 40) return;
    if (c.width !== cw * dpr || c.height !== ch * dpr) { c.width = cw * dpr; c.height = ch * dpr; }
    g.save();
    g.scale(dpr, dpr);
    g.clearRect(0, 0, cw, ch);
    g.lineJoin = "round";
    g.lineCap = "round";

    // the board is a sticker: inset just enough for its 3px ink outline and
    // the hard ink drop shadow under it to stay inside the canvas
    const size = Math.max(10, Math.min(cw - EDGE * 2, ch - EDGE * 2 - DROP));
    const cell = size / SNL_N;
    const ox = (cw - size) / 2;
    const oy = EDGE;
    const radius = Math.max(8, cell * 0.45);

    const cx = (col: number, row: number) => ox + col * cell + cell / 2;
    const cy = (col: number, row: number) => oy + (SNL_N - 1 - row) * cell + cell / 2;
    const centre = (n: number) => {
      if (n <= 0) return { x: ox + cell * 0.28, y: oy + size - cell * 0.28 }; // "start" — bottom-left corner
      const { col, row } = snlCell(n);
      return { x: cx(col, row), y: cy(col, row) };
    };

    // hard ink drop shadow
    g.fillStyle = C.ink;
    roundRectPath(g, ox, oy + DROP, size, size, radius);
    g.fill();

    // squares — a checkerboard of white and a pale tint that changes every
    // row, clipped to the rounded board
    g.save();
    roundRectPath(g, ox, oy, size, size, radius);
    g.clip();
    g.font = bodyFont(Math.max(8, cell * 0.24), 800);
    g.textBaseline = "top";
    g.textAlign = "left";
    for (let n = 1; n <= SNL_GOAL; n++) {
      const { col, row } = snlCell(n);
      const x = ox + col * cell, y = oy + (SNL_N - 1 - row) * cell;
      const pale = (col + row) % 2 === 0;
      g.fillStyle = n === SNL_GOAL ? C.green : pale ? ROW_TINTS[row % ROW_TINTS.length]! : C.white;
      // +0.5 overlap so no hairline seams show between squares
      g.fillRect(x, y, cell + 0.5, cell + 0.5);
      g.fillStyle = n === SNL_GOAL ? C.ink : C.muted;
      g.fillText(String(n), x + 3, y + 2);
    }
    g.restore();
    g.strokeStyle = C.ink;
    g.lineWidth = 3;
    roundRectPath(g, ox, oy, size, size, radius);
    g.stroke();

    // ladders — wooden rails + rungs, an ink pass under a colour pass
    const railW = Math.max(2.5, cell * 0.07);
    const rungW = Math.max(1.5, cell * 0.05);
    const ladderPath = (a: { x: number; y: number }, b: { x: number; y: number }, rungsOnly: boolean) => {
      const dx = b.x - a.x, dy = b.y - a.y;
      const len = Math.hypot(dx, dy) || 1;
      const nx = -dy / len, ny = dx / len;
      const w = cell * 0.16;
      g.beginPath();
      if (!rungsOnly) {
        g.moveTo(a.x + nx * w, a.y + ny * w); g.lineTo(b.x + nx * w, b.y + ny * w);
        g.moveTo(a.x - nx * w, a.y - ny * w); g.lineTo(b.x - nx * w, b.y - ny * w);
      } else {
        const rungs = Math.max(2, Math.round(len / (cell * 0.5)));
        for (let i = 1; i < rungs; i++) {
          const p = i / rungs;
          const mx = a.x + dx * p, my = a.y + dy * p;
          g.moveTo(mx + nx * w, my + ny * w); g.lineTo(mx - nx * w, my - ny * w);
        }
      }
    };
    Object.entries(SNL_LADDERS).forEach(([f, t]) => {
      const a = centre(Number(f)), b = centre(Number(t));
      g.strokeStyle = C.ink;
      ladderPath(a, b, true); g.lineWidth = rungW + 3; g.stroke();
      ladderPath(a, b, false); g.lineWidth = railW + 3; g.stroke();
      g.strokeStyle = LADDER;
      ladderPath(a, b, true); g.lineWidth = rungW; g.stroke();
      ladderPath(a, b, false); g.lineWidth = railW; g.stroke();
    });

    // snakes — fat coloured bodies with an ink outline and a pale belly stripe
    Object.entries(SNL_SNAKES).forEach(([h, t], i) => {
      const head = centre(Number(h)), tail = centre(Number(t));
      const col = SNAKE_COLS[i % SNAKE_COLS.length]!;
      const dx = tail.x - head.x, dy = tail.y - head.y;
      const len = Math.hypot(dx, dy) || 1;
      const nx = -dy / len, ny = dx / len;
      const wob = Math.min(cell * 1.1, len * 0.22);
      const body = () => {
        g.beginPath();
        g.moveTo(head.x, head.y);
        g.bezierCurveTo(
          head.x + dx * 0.3 + nx * wob, head.y + dy * 0.3 + ny * wob,
          head.x + dx * 0.7 - nx * wob, head.y + dy * 0.7 - ny * wob,
          tail.x, tail.y,
        );
      };
      const bw = Math.max(3.5, cell * 0.17);
      body();
      g.strokeStyle = C.ink;
      g.lineWidth = bw + 3;
      g.stroke();
      g.strokeStyle = col;
      g.lineWidth = bw;
      g.stroke();
      g.lineWidth = Math.max(1.2, cell * 0.05);
      g.strokeStyle = "rgba(255,255,255,0.45)";
      g.stroke();
      // head
      g.beginPath(); g.arc(head.x, head.y, cell * 0.21, 0, Math.PI * 2);
      g.fillStyle = col;
      g.fill();
      g.strokeStyle = C.ink;
      g.lineWidth = 1.5;
      g.stroke();
      g.fillStyle = C.white;
      g.beginPath();
      g.arc(head.x - cell * 0.065, head.y - cell * 0.04, cell * 0.055, 0, Math.PI * 2);
      g.arc(head.x + cell * 0.065, head.y - cell * 0.04, cell * 0.055, 0, Math.PI * 2);
      g.fill();
      g.fillStyle = C.ink;
      g.beginPath();
      g.arc(head.x - cell * 0.065, head.y - cell * 0.03, cell * 0.025, 0, Math.PI * 2);
      g.arc(head.x + cell * 0.065, head.y - cell * 0.03, cell * 0.025, 0, Math.PI * 2);
      g.fill();
    });

    // tokens
    const tokenPos = (slot: number) => {
      if (slot === st.mover && st.phase === "moving") {
        const p = this.path();
        const stepMs = 260 + (p.length) * 55;
        const chuteMs = st.animKind ? 620 : 0;
        if (this.animT < stepMs || chuteMs === 0) {
          const f = Math.max(0, Math.min(1, this.animT / stepMs));
          const idx = f * (p.length - 1);
          const i0 = Math.floor(idx), i1 = Math.min(p.length - 1, i0 + 1);
          const a = centre(p[i0]!), b = centre(p[i1]!);
          const t = idx - i0;
          return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
        }
        const f = Math.max(0, Math.min(1, (this.animT - stepMs) / chuteMs));
        const a = centre(st.animLand), b = centre(st.animTo);
        return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f };
      }
      return centre(st.pos[slot] ?? 0);
    };
    const p0 = tokenPos(0), p1 = tokenPos(1);
    const same = st.pos[0] === st.pos[1] && st.mover < 0;
    const off = same ? cell * 0.16 : 0;
    const tr = cell * 0.26;
    [[p0, 0, -off], [p1, 1, off]].forEach(([p, slot, dxo]: any) => {
      groundShadow(g, p.x + dxo, p.y + tr * 0.75, tr * 0.95, tr * 0.38, 0.3);
      puck(g, p.x + dxo, p.y, tr, TOK[slot]!, C.ink);
      if (slot === this.mine) {
        g.fillStyle = C.ink;
        g.font = bodyFont(Math.max(7, cell * 0.2), 900);
        g.textAlign = "center"; g.textBaseline = "middle";
        g.fillText("you", p.x + dxo, p.y + 0.5);
        g.textAlign = "left";
      }
    });

    // die (top-right — over the sparse home corner, clear of the tokens): a
    // white sticker with ink pips
    if (st.die > 0) {
      const d = cell * 1.15;
      const dx = ox + size - d - 7, dy = oy + 7;
      g.save();
      g.translate(dx + d / 2, dy + d / 2);
      if (this.tumbleT > 0) g.rotate((this.tumbleT / 480) * 6);
      g.fillStyle = C.ink;
      roundRectPath(g, -d / 2, -d / 2 + 3, d, d, d * 0.2);
      g.fill();
      roundRectPath(g, -d / 2, -d / 2, d, d, d * 0.2);
      g.fillStyle = C.white;
      g.fill();
      g.strokeStyle = C.ink;
      g.lineWidth = 2.5;
      g.stroke();
      g.fillStyle = C.ink;
      const pips: Record<number, [number, number][]> = {
        1: [[0, 0]],
        2: [[-1, -1], [1, 1]],
        3: [[-1, -1], [0, 0], [1, 1]],
        4: [[-1, -1], [1, -1], [-1, 1], [1, 1]],
        5: [[-1, -1], [1, -1], [0, 0], [-1, 1], [1, 1]],
        6: [[-1, -1], [1, -1], [-1, 0], [1, 0], [-1, 1], [1, 1]],
      };
      (pips[st.die] || []).forEach(([px, py]) => {
        g.beginPath();
        g.arc(px * d * 0.26, py * d * 0.26, d * 0.09, 0, Math.PI * 2);
        g.fill();
      });
      g.restore();
    }

    g.restore();

    // status
    const need0 = SNL_GOAL - (st.pos[0] ?? 0);
    const need1 = SNL_GOAL - (st.pos[1] ?? 0);
    const meNeed = this.mine === 0 ? need0 : need1;
    if (this.ctx.room.state.phase === "results") {
      this.statusEl.textContent = "";
    } else if (st.phase === "moving") {
      this.statusEl.textContent =
        st.animKind === 1 ? "🪜 Up you go!" : st.animKind === 2 ? "🐍 Down you slither…" : "Moving…";
    } else if (this.myTurn) {
      this.statusEl.textContent = st.die === 6 && st.turnSlot === this.mine
        ? `Rolled a 6 — go again! (${meNeed} to home)`
        : `Your roll · ${meNeed} to home`;
    } else {
      this.statusEl.textContent = "Opponent's turn…";
    }
    this.rollBtn.disabled = !this.myTurn;
    this.rollBtn.classList.toggle("live", this.myTurn);
  }

  results(ctx: ClientGameCtx): HTMLElement {
    const st = ctx.room.state.snl;
    const d = document.createElement("div");
    d.className = "arc-final";
    d.innerHTML =
      `<div class="score-big">${st.pos[0] ?? 0} — ${st.pos[1] ?? 0}</div>` +
      `<div class="arc-note">first to square 100</div>`;
    return d;
  }
}
